import React from "react";

import { produce, useTranslation } from "@opendash/core";

import { arcgisToGeoJSON } from "@esri/arcgis-to-geojson-utils";
import { Icon } from "@opendash/icons";
import {
  DataItemDimensionIdentifierInterface,
  DataSelect,
  useDataService,
} from "@opendash/plugin-timeseries";
import { Description } from "@opendash/ui";
import * as turf from "@turf/turf";
import { Button, Input, Select, Tabs } from "antd";
import Parse from "parse";
export interface GeographySelectorProps {
  type: "json" | "dimension" | "zones";
  value: any | DataItemDimensionIdentifierInterface[];
  update: (
    type: "json" | "dimension" | "zones",
    value: any | DataItemDimensionIdentifierInterface[] | string[]
  ) => void;
}
export interface GeographySelectorType {
  type: "json" | "dimension" | "zones";
  json?: any;
  dimension?: DataItemDimensionIdentifierInterface[];
}
export interface GeographySelectorTypeJSON extends GeographySelectorType {
  type: "json";
  json: any;
}
export interface GeographySelectorTypeDataDimension
  extends GeographySelectorType {
  type: "dimension";
  dimension: DataItemDimensionIdentifierInterface[];
}
export interface GeographySelectorTypeZones extends GeographySelectorType {
  type: "zones";
  zones: string[];
}

const GeographySelector = (props: GeographySelectorProps) => {
  const t = useTranslation();

  const [type, setType] = React.useState(props.type);
  const [text, setText] = React.useState(
    props.type === "json" ? JSON.stringify(props.value) : ""
  );
  const [currentDim, setCurrentDim] = React.useState<
    DataItemDimensionIdentifierInterface[]
  >(props.type === "dimension" ? props.value : []);

  const [zones, setZones] = React.useState<string[]>(
    props.type === "zones" ? props.value : []
  );

  const [availableZones, setAvailableZones] = React.useState([]);

  const init = async () => {
    try {
      const res = await new Parse.Query("MIAAS_Geographies")
        .limit(99999)
        .find();
      setAvailableZones(res);
    } catch (e) {
      console.log(e);
    }
  };

  React.useEffect(() => {
    init();
  }, []);

  return (
    <Tabs
      defaultActiveKey="json"
      activeKey={type}
      onTabClick={(tab) => {
        setType(tab as "json" | "dimension" | "zones");
      }}
    >
      <Tabs.TabPane
        key={"dimension"}
        tab={t("miaas:geographpyselector.dimensiontab.title")}
      >
        <div style={{ padding: 10 }}>
          <Description>
            {t("miaas:geographpyselector.dimensiontab.description")}
          </Description>
          <DataSelect
            style={{ width: "100%" }}
            key={"dimension-picker"}
            selectionOptions={{
              min: 1,
              types: ["Geo"],
              select: "dimension",
            }}
            selection={currentDim}
            onSelection={(selection) => {
              setCurrentDim(selection);
            }}
            // value={[currentDim] as DataItemDimensionIdentifierInterface[]}
          />
          <Button
            style={{ width: "100%", marginTop: "10px" }}
            type="primary"
            disabled={IsSameSelectionOrNull(props.value, currentDim)}
            icon={<Icon icon="fa:save" />}
            onClick={() => {
              if (props.update) {
                props.update("dimension", currentDim);
              }
            }}
          />
        </div>
      </Tabs.TabPane>
      <Tabs.TabPane
        key={"zones"}
        tab={t("miaas:geographpyselector.zonestab.title")}
      >
        <div style={{ padding: 10 }}>
          <Description>
            {t("miaas:geographpyselector.zonestab.description")}
          </Description>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="Zonen auswählen"
            defaultValue={[]}
            value={zones}
            onChange={(e) => {
              setZones(e as string[]);
            }}
            options={availableZones.map((zone) => {
              return { label: zone.get("label"), value: zone.id, key: zone.id };
            })}
          />
          <Button
            style={{ width: "100%", marginTop: "10px" }}
            type="primary"
            disabled={(props.value?.length ?? 0) === (zones?.length ?? 0)}
            icon={<Icon icon="fa:save" />}
            onClick={() => {
              if (props.update) {
                props.update("zones", zones);
              }
            }}
          />
        </div>
      </Tabs.TabPane>
      <Tabs.TabPane
        key={"json"}
        tab={t("miaas:geographpyselector.geotab.title")}
      >
        <Description>
          {t("miaas:geographpyselector.jsontab.description")}
        </Description>
        <div style={{ padding: 10 }}>
          <Input.TextArea
            style={{ height: "400px" }}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
          />

          <Button
            style={{ width: "100%", marginTop: "10px" }}
            type="primary"
            disabled={!IsJsonString(text)}
            icon={<Icon icon="fa:save" />}
            onClick={() => {
              if (props.update) {
                props.update("json", JSON.parse(text));
              }
            }}
          />
        </div>
      </Tabs.TabPane>
    </Tabs>
  );
};
function IsJsonString(str) {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}
function IsSameSelectionOrNull(
  old: DataItemDimensionIdentifierInterface[],
  newDim: DataItemDimensionIdentifierInterface[]
) {
  if (!old && !newDim) return true;
  if (!old && newDim) return false;
  if (old && !newDim) return false;

  if ((old && !newDim) || (!old && newDim)) return false;
  if (old.length !== newDim.length) return false;

  for (const idx in old) {
    const oldDim = old[idx];
    const cNewDim = newDim[idx];
    if (
      oldDim[0] !== cNewDim[0] ||
      oldDim[1] !== cNewDim[1] ||
      oldDim[2] !== cNewDim[2]
    ) {
      return false;
    }
  }
  return true;
}

export async function getFeatureCollections(
  type: "json" | "dimension" | "zones",
  value: any[] | DataItemDimensionIdentifierInterface | string[]
) {
  if (!value) return null;
  const DataService = useDataService();
  let memoDisticts = null;
  if (type === "json") {
    let toUse = value;
    if (!value.length) {
      toUse = [value];
    }
    memoDisticts = toUse.map((district) => {
      try {
        if (
          district.wkid ||
          district.wkt ||
          district.spatialReference.wkid ||
          district.spatialReference.wkt
        ) {
          return arcgisToGeoJSON(district);
        }
      } catch (error) {
        return district;
      }
      return district;
    });
  }
  if (type === "dimension") {
    const features = (value as DataItemDimensionIdentifierInterface[]).map(
      (didId) => {
        const item = DataService._getOrThrowSync(didId[0], didId[1]);
        const itemvalue = DataService._getValueOrThrowSync(item);
        let geoValue = itemvalue.value[didId[2]];
        if (geoValue.type.toLowerCase() !== "featurecollection") {
          geoValue = turf.featureCollection(geoValue);
          if (!Array.isArray(geoValue.features)) {
            geoValue.features = [geoValue.features];
          }
        }

        geoValue.features = geoValue.features.map((feature) => {
          feature.properties.id = JSON.stringify([
            didId[0],
            didId[1],
            didId[2],
          ]);
          if (!feature.properties.name) {
            feature.properties.name = DataService.getItemName(
              item,
              didId[2],
              false
            );
          }
          return feature;
        });
        return geoValue;
      }
    );

    memoDisticts = features;
  }
  if (type === "zones") {
    const res = await new Parse.Query("MIAAS_Geographies")
      .limit(99999)
      .containedIn("objectId", value as string[])
      .find();
    memoDisticts = res.map((zone) => {
      const geo = zone.get("geo");
      if (geo.type.toLowerCase() !== "featurecollection") {
        return turf.featureCollection([geo]);
      }
      geo.features = geo.features.map((feature) => {
        if (
          !feature.properties ||
          !feature.properties.name ||
          !feature.properties.objectId
        ) {
          feature.properties = feature.properties || {};
          feature.properties.name = zone.get("label");
          feature.properties.objectId = zone.id;
        }
        return feature;
      });
      return geo;
    });
  }

  memoDisticts = produce(memoDisticts, (arrayOfFeatures) => {
    for (const draft of arrayOfFeatures) {
      draft.features = draft.features.map((district, idx) => {
        district.properties = district.properties || {};
        district.bbox = [
          Number.MAX_SAFE_INTEGER,
          Number.MAX_SAFE_INTEGER,
          Number.MIN_SAFE_INTEGER,
          Number.MIN_SAFE_INTEGER,
        ];

        turf.coordAll(district).forEach(([lon, lat]) => {
          district.bbox[0] = Math.min(lon, district.bbox[0]); // minLon
          district.bbox[1] = Math.min(lat, district.bbox[1]); // minLat
          district.bbox[2] = Math.max(lon, district.bbox[2]); // maxLon
          district.bbox[3] = Math.max(lat, district.bbox[3]); // maxLat
        });

        if (!district.properties) {
          district.properties = {};
        }
        if (!district.properties.name) {
          district.properties.name = "District-" + idx;
        }

        return district;
      });
    }
  });

  return memoDisticts;
}

export default GeographySelector;
